import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { ArrowLeft, Award, CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp, Users, BookOpen, RefreshCw, Download, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExamResultsContent = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [expandedSchools, setExpandedSchools] = useState({});

  const fetchResults = async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      
      const response = await fetch(`${BACKEND_URL}/api/exam-management/results`);
      if (!response.ok) throw new Error('Failed to fetch exam results');
      
      const result = await response.json();
      setData(result);
      
      if (showRefreshToast) {
        toast.success('Data refreshed successfully');
      }
    } catch (error) {
      console.error('Error fetching exam results:', error);
      toast.error('Failed to load exam results');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchResults();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchResults(), 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleSchool = (key) => {
    setExpandedSchools(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/export/exam-results/${format}`);
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')?.split('filename=')[1] || `exam_results.${format}`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/"/g, '');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported to ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export to ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // If it's already a formatted EST string (contains "EST"), display as-is
    if (typeof dateStr === 'string' && dateStr.includes('EST')) {
      return dateStr;
    }
    // Fallback for older records without EST format
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) + ' (Local)';
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 pt-14 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#5f7c8a]" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-14">
        {/* Hero Banner */}
        <div className="bg-[#5f7c8a] py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-light text-white tracking-[0.15em]">
              EXAM MANAGEMENT
            </h1>
            <div className="w-48 h-1 bg-white/50 mx-auto mt-4" />
            <p className="text-white/80 mt-4">Real-time exam results dashboard</p>
          </div>
        </div>

        {/* Back Link & Refresh */}
        <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between flex-wrap gap-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={exporting || !data?.schools?.length}
              className="gap-2"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              Export CSV
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleExport('excel')}
              disabled={exporting || !data?.schools?.length}
              className="gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet size={16} />
              Export Excel
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fetchResults(true)}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="max-w-6xl mx-auto px-4 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Exams Taken</p>
                    <p className="text-2xl font-bold text-gray-800">{data?.total_exams || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Passed</p>
                    <p className="text-2xl font-bold text-green-600">
                      {data?.schools?.reduce((sum, s) => sum + s.passed, 0) || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="bg-red-100 p-3 rounded-full">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Failed</p>
                    <p className="text-2xl font-bold text-red-600">
                      {data?.schools?.reduce((sum, s) => sum + s.failed, 0) || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Results by School */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-medium text-gray-800 flex items-center gap-2">
                <Award className="text-[#5f7c8a]" />
                Exam Results by School
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Last updated: {data?.last_updated ? formatDate(data.last_updated) : 'N/A'}
              </p>
            </div>

            {data?.schools?.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No exam results available yet.</p>
                <p className="text-sm">Results will appear here as students complete exams.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data?.schools?.map((school) => {
                  const key = `${school.school_id}_${school.exam_type}`;
                  const isExpanded = expandedSchools[key];
                  
                  return (
                    <div key={key}>
                      {/* School Header - Clickable */}
                      <div 
                        className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleSchool(key)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-2 h-12 rounded ${school.exam_type === 'final' ? 'bg-purple-500' : 'bg-blue-500'}`} />
                          <div>
                            <h3 className="font-semibold text-gray-800">{school.school_name}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                school.exam_type === 'final' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {school.exam_type === 'final' ? 'Final Exam' : 'Mock Exam'}
                              </span>
                              <span>{school.total_candidates} candidate(s)</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="flex items-center gap-4">
                              <span className="text-green-600 font-medium">{school.passed} Passed</span>
                              <span className="text-red-600 font-medium">{school.failed} Failed</span>
                            </div>
                            <p className="text-sm text-gray-500">Pass Rate: {school.pass_rate}%</p>
                          </div>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      
                      {/* Expanded Results Table */}
                      {isExpanded && (
                        <div className="bg-gray-50 p-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="font-semibold">Candidate Name</TableHead>
                                <TableHead className="font-semibold">Branch</TableHead>
                                <TableHead className="font-semibold">Score</TableHead>
                                <TableHead className="font-semibold">Status</TableHead>
                                <TableHead className="font-semibold">Date Taken</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {school.results.map((result, idx) => (
                                <TableRow key={idx} className="bg-white">
                                  <TableCell className="font-medium">{result.candidate_name}</TableCell>
                                  <TableCell>{result.branch || 'N/A'}</TableCell>
                                  <TableCell>{result.score}%</TableCell>
                                  <TableCell>
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
                                      result.passed 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                    }`}>
                                      {result.passed ? (
                                        <><CheckCircle size={12} /> PASS</>
                                      ) : (
                                        <><XCircle size={12} /> FAIL</>
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-gray-500 text-sm">
                                    {formatDate(result.date_taken)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const ExamResultsPage = () => {
  return (
    <AdminProtected title="Exam Management Dashboard">
      <ExamResultsContent />
    </AdminProtected>
  );
};

export default ExamResultsPage;
